import React, { type Dispatch, type SetStateAction, useContext } from 'react';

import type { InstanceContextQuery } from '@/common/__generated__/graphql';

export type SiteContextScenario = InstanceContextQuery['scenarios'][0];

export type SiteI18nConfig = {
  locale: string;
  defaultLocale: string;
  supportedLocales: string[];
};

export type SiteContextType = {
  instanceId?: string;
  title: string;
  apolloConfig: {
    instanceHostname: string;
    instanceIdentifier: string;
  };

  basePath: string;
  assetPrefix: string;
  i18n: SiteI18nConfig;
  iconBase: string;
  ogImage: string;
  baselineName: string | null | undefined;
  minYear: number;
  maxYear: number;
  referenceYear: number | null;
  targetYear: number;
  latestMetricYear: number;
  owner: string | null;

  scenarios: SiteContextScenario[];
  availableNormalizations: InstanceContextQuery['availableNormalizations'];
  parameters: InstanceContextQuery['parameters'];
  menuPages: InstanceContextQuery['menuPages'];
  footerPages: InstanceContextQuery['footerPages'];
  additionalLinkPages: InstanceContextQuery['additionalLinkPages'];
  watchLink?: {
    title: string | { [key: string]: string };
    url: string | { [key: string]: string };
  } | null;
};

export type SiteContextWithSetterType = [
  SiteContextType,
  Dispatch<SetStateAction<SiteContextType>>,
];

const SiteContext = React.createContext<SiteContextWithSetterType>(null!);

export const useSite = () => {
  const context = useContext(SiteContext);

  if (!context) {
    throw new Error('useSite called without a SiteContext');
  }

  return context[0];
};

export const useSiteOrNull = () => {
  const context = useContext(SiteContext);
  if (!context) return null;
  return context[0];
};

/**
 * For legacy reasons, expose the full site context with context value and
 * setter in a separate hook. In future we could migrate all uses of useSite to this.
 */
export const useSiteWithSetter = () => {
  return useContext(SiteContext);
};

export default SiteContext;
